import React from 'react'
import { useRouter } from 'next/router'
import { GoChevronUp, GoChevronDown } from "react-icons/go"
import classNames from 'classnames'
import querystring from "querystring";
import orderBy from 'lodash/orderBy';
import { extractAxiosError } from '../../../utils/error'
import basicColors from '../../../constants/basic_colors'
import Api from '../../../service/api'
import HeadTitle from '../../../components/head_title'
import Layout from '../../../components/layout/layout'
import Breadcrumbs from '../../../components/breadcrumbs'
import TimeoutAlert from '../../../components/timeout_alert'
import Loading from '../../../components/loading'
import { useCategoriesWrapper } from '../../../service/category'
import * as ObjectUtils from '../../../utils/object'
import styles from '../../../styles/product_listing.module.scss'
import usePagination from '../../../hooks/pagination'
import ProductListingCard from '../../../components/product_listing_card'
import {useRegionLinkPrefix} from '../../../hooks/region'
import {isBlank, isUpperCase} from "../../../utils/string";
import {excludeProperties} from "../../../utils/object";
import {urlForReplacedCountryCode} from "../../../lib/region";
import {getQueryFromQueries, useQuery} from "../../../hooks/use_query";
import {convertQueryToParams} from "../../../utils/url";
import {ProductListing} from "../../../types/models/product";
import {CategorySpread} from "../../../types/api/responses/product";
import * as gtag from '../../../lib/gtag';

interface SiblingCategory extends CategorySpread {
  name: string;
}

function SearchProducts() {
  const router = useRouter()

  if (router.isFallback) {
    return <Loading />
  }

  const regionLinkPrefix = useRegionLinkPrefix()
  const categories = useCategoriesWrapper()
  const categoriesLoaded = !!categories
  const {query} = router
  const [pageInt, paginator, setTotalPages] = usePagination()

  const queryTextSearch = useQuery('text');
  const [textSearch, setTextSearch] = React.useState('');
  const [products, setProducts] = React.useState<ProductListing[] | null>(null);
  const [siblingCategories, setSiblingCategories] = React.useState<SiblingCategory[]>([]);
  const [minPrice, setMinPrice] = React.useState('');
  const [maxPrice, setMaxPrice] = React.useState('');
  const [selectedColors, setSelectedColors] = React.useState(new Set());
  const [selectedCategoryIds, setSelectedCategoryIds] = React.useState(new Set());

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [isHidden, setIsHidden] = React.useState({
    color: true,
    price: true,
    categories: false
  })

  const handleSelectColor = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedColors = new Set(selectedColors)
    if (e.target.checked) {
      newSelectedColors.add(e.target.value);
    } else {
      newSelectedColors.delete(e.target.value);
    }
    setSelectedColors(newSelectedColors);
  }, [selectedColors]);

  const handleSelectCategory  = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedCategoryIds = new Set(selectedCategoryIds)
    if (e.target.checked) {
      newSelectedCategoryIds.add(e.target.value)
    } else {
      newSelectedCategoryIds.delete(e.target.value)
    }
    setSelectedCategoryIds(newSelectedCategoryIds)
  }, [selectedCategoryIds])

  const handleFilter = React.useCallback(() => {
    const params = new URLSearchParams()

    if (query.merchant) {
      params.set('merchant', Array.isArray(query.merchant) ? query.merchant[0] : query.merchant);
    }

    if (!isBlank(textSearch)) {
      params.set('text', textSearch)
    }

    if (maxPrice.length > 0) {
      params.set('maxPrice', maxPrice)
    }
    if (minPrice.length > 0) {
      params.set('minPrice', minPrice)
    }
    if (selectedColors.size > 0) {
      params.set('colors', Array.from(selectedColors).join(','))
    }

    if (selectedCategoryIds.size > 0) {
      params.set('categories', Array.from(selectedCategoryIds).join(','))
    }

    params.set('page', '1');

    router.push(`${regionLinkPrefix}/product/search?${params.toString()}`)
  }, [regionLinkPrefix, query.merchant, textSearch, maxPrice, minPrice, selectedColors, selectedCategoryIds, pageInt])

  const handleSearchSubmit = React.useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (isBlank(textSearch)) {
      router.push(regionLinkPrefix)
      return
    }

    router.push(`${regionLinkPrefix}/product/search?text=${encodeURIComponent(textSearch)}`)
  }, [textSearch, regionLinkPrefix])

  const toggleSection = React.useCallback((key: keyof typeof isHidden) => () => {
    const newIsHidden = Object.assign({}, isHidden, {
      [key]: !isHidden[key],
    })
    setIsHidden(newIsHidden)
  }, [isHidden])

  React.useEffect(() => {
    const {
      countryCode,
      ...queryWithoutCountryCode
    } = query

    if (!router.isReady) {
      return
    }

    const countryCodeStr = Array.isArray(countryCode) ? countryCode[0] : countryCode;
    if (countryCodeStr && !isUpperCase(countryCodeStr)) {
      const url = urlForReplacedCountryCode(countryCodeStr.toUpperCase(), router.pathname, querystring.stringify(excludeProperties(router.query, 'countryCode')))
      router.push(url)
      return
    }

    if (ObjectUtils.isEmpty(queryWithoutCountryCode)) {
      return
    }

    setTextSearch(getQueryFromQueries(queryWithoutCountryCode, 'text') ?? '')
    setMinPrice(getQueryFromQueries(queryWithoutCountryCode, 'minPrice') ?? '')
    setMaxPrice(getQueryFromQueries(queryWithoutCountryCode, 'maxPrice') ?? '')
    const colors = getQueryFromQueries(queryWithoutCountryCode, 'colors')
    if (!colors) {
      setSelectedColors(new Set())
    } else {
      setSelectedColors(new Set(colors.split(',')))
    }

    const queryCategories = getQueryFromQueries(queryWithoutCountryCode, 'categories')
    if (!queryCategories) {
      setSelectedCategoryIds(new Set())
    } else {
      setSelectedCategoryIds(new Set(queryCategories.split(',')))
    }
  }, [router.isReady, query])

  React.useEffect(() => {
    const {
      countryCode,
      ...queryWithoutCountryCode
    } = query
    if (!categoriesLoaded || !router.isReady) {
      return
    }

    const params = convertQueryToParams(query);
    const searchText = getQueryFromQueries(query, 'text');
    const isSearchTextBlank = isBlank(searchText);
    if (isSearchTextBlank) {
      params.delete('text')
      delete queryWithoutCountryCode.text
    }

    if (ObjectUtils.isEmpty(queryWithoutCountryCode)) {
      router.push(regionLinkPrefix)
      return
    }

    if (countryCode) {
      if (Array.isArray(countryCode)) {
        params.set('countryCode', countryCode[0]);
      } else {
        params.set('countryCode', countryCode);
      }
    }

    params.set('categorySpread', '1');

    Api.product.search(params)
      .then((res) => {
        setTotalPages(res.data.totalPages);
        setProducts(res.data.products);
        if (res.data.categorySpread) {
          const spreadCategories = res.data.categorySpread.map((spread) => {
            const cat = categories.getCategoryById(spread.id)
            return {
              id: cat.id,
              name: cat.name,
              count: spread.count
            }
          })
          const sortedSpreadCategories = orderBy(spreadCategories, ['count', 'name'], ['desc', 'asc']);
          setSiblingCategories(sortedSpreadCategories)
        }

        if (searchText) {
          gtag.search(searchText);
        }
      })
      .catch(error => {
        setErrorMessage(extractAxiosError(error))
      })
  }, [router.isReady, query, categoriesLoaded])

  let content
  if (products !== null) {
    content = <React.Fragment>
      <h2 className="text-primary">Search Products</h2>
      <hr/>
      <div className="row">
        <div className="col-md-4 col-lg-3 mb-4">
          <div className={`card ${styles.filters}`}>
            <div className="card-body">
              <>
                <h4 className={styles.filters_header} onClick={toggleSection('categories')}>
                  Categories

                  {isHidden['categories'] ? (
                    <GoChevronDown className="float-right text-primary" />
                  ) : (
                    <GoChevronUp className="float-right text-primary" />
                  )}
                </h4>

                {siblingCategories.length > 0 ? (
                  <div className={classNames({
                    'd-none': isHidden['categories']
                  })}>
                    {siblingCategories.map((c, index) => {
                      const id = `category-spread-${index}`
                      return <div className="form-check" key={index}>
                        <input
                          id={id}
                          className="form-check-input"
                          type="checkbox"
                          value={c.id}
                          checked={selectedCategoryIds.has(c.id.toString())}
                          onChange={handleSelectCategory} />
                        <label className="form-check-label" htmlFor={id}>
                          {c.name} ({c.count})
                        </label>
                      </div>
                    })}
                  </div>
                ) : (
                  <p>-</p>
                )}

                <hr/>
              </>

              <h4 className={styles.filters_header} onClick={toggleSection('price')}>
                Price 
                {isHidden['price'] ? (
                  <GoChevronDown className="float-right text-primary" />
                ) : (
                  <GoChevronUp className="float-right text-primary" />
                )}
              </h4>
              <div className={classNames({
                'd-none': isHidden['price']
              })}>
                <div className="form-group">
                  <label className="form-label">Min Price</label>
                  <input type="number" className="form-control" onChange={e => setMinPrice(e.target.value)} value={minPrice} />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Price</label>
                  <input type="number" className="form-control" onChange={e => setMaxPrice(e.target.value)} value={maxPrice}/>
                </div>
              </div>
              <hr/>

              <h4 className={styles.filters_header} onClick={toggleSection('color')}>
                Color 
                {isHidden['color'] ? (
                  <GoChevronDown className="float-right text-primary" />
                ) : (
                  <GoChevronUp className="float-right text-primary" />
                )}
              </h4>
              <div className={classNames({
                'd-none': isHidden['color']
              })}>
                <div className="form-group">
                  {basicColors.map(({name, hex}) => (
                    <div className="form-check" key={name}>
                      <input 
                        id={`color-filter-${name}`} 
                        className="form-check-input" 
                        type="checkbox" 
                        value={name} 
                        checked={selectedColors.has(name)}
                        onChange={handleSelectColor} />
                      <label className="form-check-label" htmlFor={`color-filter-${name}`}>
                        <div className={styles.filterColorPreview} style={{backgroundColor: hex}}/>
                        {name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <hr/>

              <button type="button" className="btn btn-primary" onClick={handleFilter}>Filter</button>
            </div>
          </div>
        </div>
        <div className="col-md-8 col-lg-9">
        	<form onSubmit={handleSearchSubmit}>
            <div className="row">
              <div className="col-md">
        		    <input type="text" className="form-control" value={textSearch} onChange={(e) => setTextSearch(e.target.value)} />
        	      <p>Searching for "{queryTextSearch}"</p>
              </div>
              <div className="col-md-auto">
        		    <button type="submit" className="btn btn-primary">Search</button>
              </div>
            </div>
        	</form>
          <div className="row">
              {products.map((product, index) => (
            		<div className="col-6 col-lg-3 mb-4" key={index}>
	                <ProductListingCard
                    product={product}
                  />
            		</div>
              ))}
          </div>
          {paginator}
        </div>
      </div>
    </React.Fragment>
  } else {
    content = <Loading />
  }

  return <Layout>
    <HeadTitle title="Search Products" />

    <Breadcrumbs
      currentPage="Search"
    />

    <TimeoutAlert
      message={errorMessage}
      onHide={() => setErrorMessage(null)}
    />

    {content}
  </Layout>
}

export default SearchProducts

export { getStaticPaths } from '../index';
export { basicGetStaticProps as getStaticProps } from '../../../lib/static_props'
